import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { all_routes } from "../../../Router/all_routes";
import {
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  Info,
  PlusCircle,
  X,
} from "feather-icons-react/build/IconComponents";
import { useDispatch, useSelector } from "react-redux";
import { setToogleHeader } from "../../../core/redux/action";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import Swal from "sweetalert2";
import ImageWithBasePath from "../../../core/img/imagewithbasepath";
import { api_post, api_get } from "../../../helpers/index";

const EditPrize = () => {
  const route = all_routes;
  const dispatch = useDispatch();
  const data = useSelector((state) => state.toggle_header);
  const navigate = useNavigate();
  const location = useLocation();

  // Get prize_id from query string
  const searchParams = new URLSearchParams(location.search);
  const prize_id = searchParams.get("prize_id");

  // State for prize details
  const [prizeName, setPrizeName] = useState("");
  const [description, setDescription] = useState("");
  const [imageBase64, setImageBase64] = useState("");
  const [isImageVisible, setIsImageVisible] = useState(false);
  const [errors, setErrors] = useState({});
  const [prizeType, setPrizeType] = useState("gift");
  const [prizeValue, setPrizeValue] = useState({
    from_value: 0,
    to_value: 0
  });

  useEffect(() => {
    // Fetch prize details when component mounts
    const fetchPrizeDetails = async () => {
      try {
        const response = await api_get(`spinwheel/prize/get/${prize_id}`);
        if (response.status === 1) {
          const { name, description, image, prize_type, prize_value } = response.data;
          setPrizeName(name);
          setDescription(description);
          setImageBase64(image);
          setIsImageVisible(!!image);
          setPrizeType(prize_type || "gift");
          setPrizeValue(prize_value || { from_value: 0, to_value: 0 });
        } else {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: "Failed to load prize details.",
          });
        }
      } catch (error) {
        console.error("Error fetching prize details:", error);
      }
    };

    if (prize_id) {
      fetchPrizeDetails();
    }
  }, [prize_id]);

  const renderCollapseTooltip = (props) => (
    <Tooltip id="refresh-tooltip" {...props}>
      Collapse
    </Tooltip>
  );

  const handleRemovePrize = () => {
    setIsImageVisible(false);
    setImageBase64("");
  };

  // Convert image to base64 and display
  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImageBase64(reader.result);
        setIsImageVisible(true);
      };
      reader.readAsDataURL(file);
    }
  };

  // Validate input fields
  const validateInputs = () => {
    const newErrors = {};
    if (!prizeName.trim()) newErrors.prizeName = "Prize name is required.";
    if (!description.trim()) newErrors.description = "Description is required.";
    if (description.trim().length > 60) newErrors.description = "Description must not exceed 60 characters.";
    if (!imageBase64) newErrors.image = "Prize image is required.";
    
    // Validate prize value for credit type
    if (prizeType === "credit") {
      if (typeof prizeValue.from_value !== "number" || typeof prizeValue.to_value !== "number") {
        newErrors.prizeValue = "Prize values must be numbers";
      }
      if (prizeValue.from_value < 0 || prizeValue.to_value < 0) {
        newErrors.prizeValue = "Prize values cannot be negative";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle prize type change
  const handlePrizeTypeChange = (e) => {
    const type = e.target.value;
    setPrizeType(type);
    // Set default prize value based on type
    if (type === "gift" || type === "empty") {
      setPrizeValue({
        from_value: 0,
        to_value: 0
      });
    }
  };

  // Handle prize value change
  const handlePrizeValueChange = (e, field) => {
    const value = parseFloat(e.target.value);
    setPrizeValue(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle saving the updated prize
  const handleSavePrize = async () => {
    if (!validateInputs()) {
      return;
    }

    const body = {
      name: prizeName,
      description: description,
      image: imageBase64,
      prize_type: prizeType,
      prize_value: prizeValue
    };
    try {
      const result = await api_post(`spinwheel/prize/update/${prize_id}`, body);
      if (result.status === 1) {
        // Show success message with SweetAlert2 and navigate to prize list
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Prize updated successfully!",
        }).then(() => {
          navigate(route.prizeList);
        });
      } else {
        // Show error message with SweetAlert2
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to update prize. Please try again later.",
        });
      }
    } catch (error) {
      console.error("Error updating prize:", error);
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Edit Prize</h4>
              <h6>Update prize details</h6>
            </div>
          </div>
          <ul className="table-top-head">
            <li>
              <div className="page-btn">
                <Link to={route.prizeList} className="btn btn-secondary">
                  <ArrowLeft className="me-2" />
                  Back to Prize
                </Link>
              </div>
            </li>
            <li>
              <OverlayTrigger placement="top" overlay={renderCollapseTooltip}>
                <Link
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  title="Collapse"
                  id="collapse-header"
                  className={data ? "active" : ""}
                  onClick={() => {
                    dispatch(setToogleHeader(!data));
                  }}
                >
                  <ChevronUp className="feather-chevron-up" />
                </Link>
              </OverlayTrigger>
            </li>
          </ul>
        </div>
        <form>
          <div className="card">
            <div className="card-body add-prize pb-0">
              <div className="accordion-card-one accordion" id="accordionExample">
                <div className="accordion-item">
                  <div className="accordion-header" id="headingOne">
                    <div
                      className="accordion-button"
                      data-bs-toggle="collapse"
                      data-bs-target="#collapseOne"
                      aria-controls="collapseOne"
                    >
                      <div className="addprize-icon">
                        <h5>
                          <Info className="add-info" />
                          <span>Prize Information</span>
                        </h5>
                        <Link to="#">
                          <ChevronDown className="chevron-down-add" />
                        </Link>
                      </div>
                    </div>
                  </div>
                  <div
                    id="collapseOne"
                    className="accordion-collapse collapse show"
                    aria-labelledby="headingOne"
                    data-bs-parent="#accordionExample"
                  >
                    <div className="accordion-body">
                      <div className="row">
                        <div className="col-lg-4 col-sm-6 col-12">
                          <div className="mb-3 add-prize">
                            <label className="form-label">Prize Name</label>
                            <input
                              type="text"
                              className="form-control"
                              value={prizeName}
                              onChange={(e) => setPrizeName(e.target.value)}
                            />
                            {errors.prizeName && (
                              <div className="text-danger mt-1">{errors.prizeName}</div>
                            )}
                          </div>
                        </div>
                        <div className="col-lg-4 col-sm-6 col-12">
                          <div className="mb-3 add-prize">
                            <label className="form-label">Prize Type</label>
                            <select
                              className="form-control"
                              value={prizeType}
                              onChange={handlePrizeTypeChange}
                            >
                              <option value="gift">Gift</option>
                              <option value="credit">Credit</option>
                              <option value="empty">Empty</option>
                            </select>
                          </div>
                        </div>
                        {prizeType === "credit" && (
                          <div className="col-lg-12">
                            <div className="row">
                              <div className="col-lg-6 col-sm-6 col-12">
                                <div className="mb-3 add-prize">
                                  <label className="form-label">From Value</label>
                                  <input
                                    type="number"
                                    className="form-control"
                                    value={prizeValue.from_value}
                                    onChange={(e) => handlePrizeValueChange(e, "from_value")}
                                  />
                                </div>
                              </div>
                              <div className="col-lg-6 col-sm-6 col-12">
                                <div className="mb-3 add-prize">
                                  <label className="form-label">To Value</label>
                                  <input
                                    type="number"
                                    className="form-control"
                                    value={prizeValue.to_value}
                                    onChange={(e) => handlePrizeValueChange(e, "to_value")}
                                  />
                                </div>
                              </div>
                            </div>
                            {errors.prizeValue && (
                              <div className="text-danger mt-1">{errors.prizeValue}</div>
                            )}
                          </div>
                        )}
                        <div className="col-lg-12">
                          <div className="input-blocks summer-description-box transfer mb-3">
                            <label>Description</label>
                            <textarea
                              className="form-control h-100"
                              rows={5}
                              value={description}
                              onChange={(e) => setDescription(e.target.value)}
                            />
                            <p className="mt-1">Maximum 60 Characters</p>
                            {errors.description && (
                              <div className="text-danger mt-1">{errors.description}</div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="accordion-body">
                      <div className="tab-content" id="pills-tabContent">
                        <div
                          className="tab-pane fade show active"
                          id="pills-home"
                          role="tabpanel"
                          aria-labelledby="pills-home-tab"
                        >
                          <div
                            className="accordion-card-one accordion"
                            id="accordionExample3"
                          >
                            <div className="accordion-item">
                              <div
                                className="accordion-header"
                                id="headingThree"
                              >
                                <div
                                  className="accordion-button"
                                  data-bs-toggle="collapse"
                                  data-bs-target="#collapseThree"
                                  aria-controls="collapseThree"
                                >
                                  <div className="addprize-icon list">
                                    <h5>
                                      <i
                                        data-feather="image"
                                        className="add-info"
                                      />
                                      <span>Image</span>
                                    </h5>
                                    <Link to="#">
                                      <ChevronDown className="chevron-down-add" />
                                    </Link>
                                  </div>
                                </div>
                              </div>
                              <div
                                id="collapseThree"
                                className="accordion-collapse collapse show"
                                aria-labelledby="headingThree"
                                data-bs-parent="#accordionExample3"
                              >
                                <div className="accordion-body">
                                  <div className="text-editor add-list add">
                                    <div className="col-lg-12">
                                      <div className="add-choosen">
                                        <div className="input-blocks">
                                          <div className="image-upload">
                                            <input
                                              type="file"
                                              onChange={handleImageUpload}
                                            />
                                            <div className="image-uploads">
                                              <PlusCircle className="plus-down-add me-0" />
                                              <h4>Add Image</h4>
                                            </div>
                                          </div>
                                        </div>
                                        {errors.image && (
                                          <div className="text-danger mt-1">{errors.image}</div>
                                        )}
                                        {isImageVisible && (
                                          <div className="phone-img">
                                            <ImageWithBasePath
                                              src={imageBase64}
                                              alt="Uploaded Image"
                                            />
                                            <Link
                                              to="#"
                                              onClick={(event) => {
                                                event.preventDefault(); // Prevents navigation and page reload
                                                handleRemovePrize(); // Executes your custom logic
                                              }}
                                            >
                                              <X className="x-square-add remove-prize" />
                                            </Link>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-lg-12">
            <div className="btn-addprize mb-4">
              <button
                type="button"
                className="btn btn-cancel me-2"
                onClick={() => {
                  navigate(route.prizeList);
                }}
              >
                Cancel
              </button>
              <button type="button" className="btn btn-submit" onClick={handleSavePrize}>
                Save Prize
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditPrize;
